import { CircleAlert, PencilIcon, RotateCcwIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { useIntl } from 'react-intl';
import { useAiChatOptionPick } from '../aiChatOptionPickContext';
import type { AiChatFailedSend } from '../hooks/useAiChatController';
import { Spinner } from './progress/Spinner';

interface AiChatSendErrorProps {
    failure: AiChatFailedSend;
    /** A retry of this send is running: Retry stays where it is, busy, so focus does not drop. */
    isRetrying: boolean;
    onRetry: () => void;
    /** Removes the failed turn and returns its question. */
    onEditMessage: () => string;
}

/**
 * A send that got no answer: what did not happen, then what to do, with Retry and Edit message. It reaches the
 * composer and the status region through the conversation's option-pick context, so it sits inside that provider.
 */
export default function AiChatSendError({ failure, isRetrying, onRetry, onEditMessage }: AiChatSendErrorProps) {
    const intl = useIntl();
    const { announce, setDraft, focus } = useAiChatOptionPick();
    const retrying = intl.formatMessage({ id: 'ai-chat.error.retrying' });

    const retry = () => {
        if (isRetrying) return;
        announce(retrying);
        onRetry();
    };

    const editMessage = () => {
        setDraft(onEditMessage());
        focus();
    };

    return (
        <div role="alert" className="bg-danger-tint text-danger-ink flex gap-3 rounded-xl px-3.5 py-3">
            <CircleAlert className="mt-px size-5 shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex flex-col">
                    <p className="m-0 font-semibold">{intl.formatMessage({ id: 'ai-chat.error.not-received' })}</p>
                    <p className="text-meta m-0">
                        {failure.noResponse
                            ? intl.formatMessage({ id: 'ai-chat.error.check-connection' })
                            : failure.message}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="secondary"
                        aria-disabled={isRetrying || undefined}
                        onClick={retry}
                        className="text-meta h-8 rounded-lg ps-2.5 pe-3 font-semibold aria-disabled:cursor-default"
                    >
                        {isRetrying ? (
                            <span className="grid size-3.5 place-items-center" aria-hidden>
                                <Spinner className="size-3" />
                            </span>
                        ) : (
                            <RotateCcwIcon className="size-3.5" />
                        )}
                        {isRetrying ? retrying : intl.formatMessage({ id: 'ai-chat.retry' })}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={editMessage}
                        disabled={isRetrying}
                        className="text-meta h-8 rounded-lg ps-2.5 pe-3 font-medium"
                    >
                        <PencilIcon className="size-3.5" />
                        {intl.formatMessage({ id: 'ai-chat.edit-message' })}
                    </Button>
                </div>
            </div>
        </div>
    );
}
